/**
 * PricingPage — /pricing
 *
 * Editorial Mode 1-adjacent surface. "Choose how the night will be
 * preserved ✦" — three editions presented as archival plates, NOT a
 * SaaS pricing comparison.
 *
 * Per Craig's directive:
 *   • The checkout flow should feel like commissioning preservation,
 *     selecting how the night will live on, choosing the scale of
 *     remembrance — NEVER like purchasing a software tier.
 *   • Calm confidence. Less motion than the homepage. More restraint.
 *     Think luxury hotel booking / gallery acquisition form / private
 *     members' club reservation.
 *   • Three editions: The Keepsake ✦ / The Room ✦ / The Archive ✦.
 *   • An "Included in every edition ✦" foundational block — these are
 *     the platform's preservation system, NOT comparison-grid features.
 *
 * Operational behaviour: each edition CTA POSTs to the editions
 * checkout endpoint which creates a Stripe-hosted session and
 * redirects back to /preserved/{slug} after payment.
 */
import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';
import EntrustedVeil from '../components/EntrustedVeil';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const SERIF = 'Georgia, "Cormorant Garamond", "Times New Roman", serif';
const IVORY = '#FBEED0';
const GOLD = '#D4AF5E';
const GOLD_MUTED = 'rgba(212,170,82,0.78)';
const INK_DIM = 'rgba(255,235,200,0.66)';
const WALNUT = '#0B0604';
const PARCHMENT_TINT = 'linear-gradient(180deg, rgba(28,18,10,0.96) 0%, rgba(18,10,5,0.96) 100%)';

const EDITIONS = [
  {
    id: 'keepsake',
    name: 'The Keepsake ✦',
    eyebrow: 'For intimate preservation',
    price: 30,
    description:
      'A single preserved night. The wall is small but breathing, the recap arrives the morning after, and the night settles quietly onto the archive shelf.',
    // Products are listed by display name so the pricing card can
    // show them verbatim. Mapping to backend feature flags happens
    // server-side via EDITIONS_CATALOGUE.
    products: [
      'QR Photo & Video Guest Upload',
      'Memory Snap',
      'Memory Wall',
      'Recap Letters',
      'Archive Shelf',
      'Highlight Reel',
    ],
    inheritsFrom: null,
    testId: 'edition-keepsake',
  },
  {
    id: 'room',
    name: 'The Room ✦',
    eyebrow: 'For shared atmosphere & live reaction',
    price: 60,
    featured: true,
    description:
      'The full live wall — projection, the room reacting together, sparklers on the dancefloor. The night preserved while it is still loud.',
    inheritsFrom: 'The Keepsake ✦',
    products: [
      'Invites & RSVP',
      'Audio / Video Guestbook',
      'Pocket Booth',
      'Mega Missions',
    ],
    testId: 'edition-room',
  },
  {
    id: 'archive',
    name: 'The Archive ✦',
    eyebrow: 'For the full preserved-night system',
    price: 99,
    description:
      'Every artifact. The night preserved fully — printed, filmed, spun. The platform\u2019s complete preservation system, ready for the night to return to.',
    inheritsFrom: 'The Keepsake ✦ and The Room ✦',
    products: [
      'Memory Guestbook',
      'Spin Film 360',
      'Event Film',
      'Lens Notes ✦',
      'Pro Upgrade',
    ],
    testId: 'edition-archive',
  },
];

const FOUNDATION = [
  { name: 'Memory Snap', desc: 'Selfie + handwritten note, gathered while the room is still warm.' },
  { name: 'Memory Wall', desc: 'A living wall the room arrives onto, in real time.' },
  { name: 'Recap Letters', desc: 'A quiet letter delivered the morning after, restoring the night.' },
  { name: 'Archive Shelf', desc: 'A chronological resting place the night returns to.' },
];

export default function PricingPage() {
  const navigate = useNavigate();
  const { user } = useAuth();
  // Outbound commission state machine — null | 'pressed' | 'preparing'.
  // Drives the GhostPill text/styling so the chain reads as a felt
  // sequence: idle → pressed → preparing → entrusted (veil) → handoff.
  const [commissionStage, setCommissionStage] = useState(null);
  const [busy, setBusy] = useState(null);
  // Outbound veil — only fires once the Stripe URL is in hand so a
  // slow network never turns the veil into perceived latency.
  const [veilOpen, setVeilOpen] = useState(false);

  // Promo code — entered before pressing a tile. A valid 100%-off code
  // routes through the Stripe-bypass branch on the server; partial
  // discounts reduce the Stripe amount before redirect.
  const [promoOpen, setPromoOpen] = useState(false);
  const [promoCode, setPromoCode] = useState(() => {
    try { return sessionStorage.getItem('eventdrop_pending_promo') || ''; } catch { return ''; }
  });
  const [promoStatus, setPromoStatus] = useState(null); // null | 'validating' | {valid:true, discount, description} | {error}

  // Threshold under which the "preparing" copy is suppressed entirely.
  // If the Stripe URL resolves in less than this window, we skip the
  // intermediate state and slide straight from "pressed" to the veil.
  // Prevents flicker / micro-jank on fast networks.
  const PREP_THRESHOLD_MS = 120;

  const validatePromo = async () => {
    const code = promoCode.trim();
    if (!code) { setPromoStatus(null); return; }
    setPromoStatus('validating');
    try {
      const r = await axios.post(`${API}/promo/validate`, { code });
      setPromoStatus({ valid: true, discount: r.data.discount, description: r.data.description });
      try { sessionStorage.setItem('eventdrop_pending_promo', code.toUpperCase()); } catch {}
    } catch (e) {
      setPromoStatus({ error: e?.response?.data?.detail || 'Invalid code' });
      try { sessionStorage.removeItem('eventdrop_pending_promo'); } catch {}
    }
  };

  const onChoose = async (edition) => {
    if (commissionStage) return; // ignore double-taps mid-commission
    setBusy(edition.id);
    setCommissionStage('pressed');

    // ──────────────────────────────────────────────────────────
    // Commission gating — every press must end at a Stripe-hosted
    // checkout. Two pre-flight routes branch off here:
    //
    //   • Anonymous visitor → preserve the edition, route to login.
    //     After auth they return to /pricing and the commission
    //     auto-resumes from the useEffect below.
    //
    //   • Authed visitor with no current event in session → preserve
    //     the edition, route through /begin so they NAME the night
    //     first. BeginPage.open() resumes the commission (claim +
    //     editions checkout + Stripe redirect) before the visitor
    //     ever reaches the product surface.
    //
    // The earlier behaviour silently routed both branches to /begin
    // and then to /wall-ready/{id}, bypassing payment entirely.
    // ──────────────────────────────────────────────────────────
    const targetEventId = (typeof window !== 'undefined' && sessionStorage.getItem('eventdrop_current_event_id')) || null;

    if (!user) {
      try { sessionStorage.setItem('eventdrop_pending_edition', edition.id); } catch (e) { /* ignore */ }
      navigate('/login?from=/pricing');
      return;
    }

    if (!targetEventId) {
      try { sessionStorage.setItem('eventdrop_pending_edition', edition.id); } catch (e) { /* ignore */ }
      navigate('/begin');
      return;
    }

    // Race the Stripe fetch against the prep-threshold timer so fast
    // networks never flash through "preparing" into "entrusted".
    let resolvedUrl = null;
    let isInternalRedirect = false;
    let failed = false;
    const fetchPromise = (async () => {
      try {
        const payload = { edition_id: edition.id };
        const code = promoCode.trim();
        if (code) payload.promo_code = code;
        const r = await axios.post(`${API}/events/${targetEventId}/editions/checkout`, payload);
        resolvedUrl = r.data?.url || null;
        // Server returns a same-origin /preserved/... URL when the
        // promo code zeroed the total. Detect this so we can SPA-nav
        // rather than full-page-load to Stripe.
        isInternalRedirect = !!r.data?.free_via_promo;
        if (resolvedUrl) {
          try { sessionStorage.removeItem('eventdrop_pending_promo'); } catch {}
        }
      } catch (e) {
        // eslint-disable-next-line no-console
        console.warn('edition checkout failed', e?.response?.data || e);
        const detail = e?.response?.data?.detail;
        if (detail && typeof detail === 'string') {
          setPromoStatus({ error: detail });
        }
        failed = true;
      }
    })();

    let stageTimer = setTimeout(() => {
      // If the fetch hasn't completed by the threshold, the network
      // is slow enough that we should announce the preparation —
      // converting latency into intentional ceremony.
      if (!resolvedUrl && !failed) setCommissionStage('preparing');
    }, PREP_THRESHOLD_MS);

    await fetchPromise;
    clearTimeout(stageTimer);

    if (!resolvedUrl) {
      // Fetch failed — quietly resign the chain.
      setCommissionStage(null);
      setBusy(null);
      return;
    }

    // Stripe URL is in hand — raise the veil and dwell 540ms before
    // handing off. The visitor experiences a continuous ceremony:
    // pressed → preparing → entrusted → handoff (never "Loading…").
    setVeilOpen(true);
    setTimeout(() => {
      if (isInternalRedirect && resolvedUrl.startsWith(window.location.origin)) {
        // Same-origin /preserved/... bypass — SPA-navigate so the
        // client routes through React Router without a full reload.
        const path = resolvedUrl.replace(window.location.origin, '');
        navigate(path);
      } else {
        window.location.href = resolvedUrl;
      }
    }, 540);
  };

  // Post-auth resume — if the visitor was pushed through /login after
  // pressing an edition, /pricing reads the preserved edition out of
  // sessionStorage and re-fires the commission press automatically.
  // Without this, returning users would have to remember which
  // edition they chose and tap it again.
  useEffect(() => {
    if (!user) return;
    if (commissionStage) return;
    let pending = null;
    try { pending = sessionStorage.getItem('eventdrop_pending_edition'); } catch {}
    if (!pending) return;
    const edition = EDITIONS.find((e) => e.id === pending);
    if (!edition) return;
    // Defer one tick so the page renders before the press kicks in.
    const t = setTimeout(() => { onChoose(edition); }, 60);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user]);

  return (
    <div data-testid="pricing-page" style={{ background: WALNUT, color: IVORY, minHeight: '100vh' }}>
      <Header />

      <section style={{
        padding: 'clamp(72px, 10vw, 130px) clamp(24px, 6vw, 80px) clamp(40px, 6vw, 70px)',
        textAlign: 'center',
      }}>
        <div style={{
          fontFamily: SERIF, fontStyle: 'italic',
          fontSize: 11, letterSpacing: '0.28em', textTransform: 'uppercase',
          color: GOLD_MUTED, marginBottom: 14,
        }}>
          EDITIONS OF PRESERVATION ✦
        </div>
        <h1 style={{
          margin: 0,
          fontFamily: SERIF,
          fontSize: 'clamp(32px, 5.2vw, 58px)',
          fontWeight: 500, lineHeight: 1.12,
          letterSpacing: '-0.018em',
          color: IVORY,
          textShadow: '0 0 32px rgba(212,170,82,0.18)',
        }}>
          Choose how the night will be preserved ✦
        </h1>
        <p style={{
          margin: '22px auto 0', maxWidth: 620,
          fontFamily: SERIF, fontStyle: 'italic',
          fontSize: 'clamp(15px, 1.3vw, 17px)',
          lineHeight: 1.65,
          color: INK_DIM,
        }}>
          Each edition is a commission. Same foundational preservation system —
          three scales of how alive the night becomes while it is happening,
          and how completely it is kept afterwards.
        </p>
      </section>

      <section style={{
        padding: '0 clamp(24px, 6vw, 80px) clamp(40px, 6vw, 70px)',
      }}>
        <PromoDisclosure
          open={promoOpen}
          setOpen={setPromoOpen}
          code={promoCode}
          setCode={setPromoCode}
          status={promoStatus}
          onApply={validatePromo}
        />
        <div style={{
          maxWidth: 1200, margin: '0 auto',
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit, minmax(260px, 1fr))',
          gap: 'clamp(18px, 2.4vw, 30px)',
          alignItems: 'stretch',
        }}>
          {EDITIONS.map((ed) => (
            <EditionPlate
              key={ed.id}
              edition={ed}
              busy={busy === ed.id}
              commissionStage={busy === ed.id ? commissionStage : null}
              promo={promoStatus?.valid ? promoStatus : null}
              onChoose={() => onChoose(ed)}
            />
          ))}
        </div>
      </section>

      <FoundationBlock />
      <ClosingNote />
      <Footer />
      <EntrustedVeil open={veilOpen} fadeMs={240} />
    </div>
  );
}

// ── header (Mode 2-leaning — calm confidence, no atmospheric drift) ─
const Header = () => (
  <header style={{
    padding: '20px 28px',
    background: 'rgba(11,6,4,0.72)',
    backdropFilter: 'blur(14px)',
    borderBottom: '1px solid rgba(212,170,82,0.10)',
    display: 'flex', alignItems: 'center', justifyContent: 'space-between',
    position: 'sticky', top: 0, zIndex: 20,
  }}>
    <a href="/" style={{
      fontFamily: SERIF, fontSize: 22, color: IVORY,
      textDecoration: 'none', letterSpacing: '-0.005em',
    }}>EventDrop ✦</a>
    <a href="/" style={{
      fontFamily: SERIF, fontStyle: 'italic',
      fontSize: 13, color: 'rgba(255,235,200,0.66)',
      textDecoration: 'underline',
      textDecorationColor: 'rgba(212,170,82,0.28)',
      textUnderlineOffset: 4,
    }}>← Back</a>
  </header>
);

// ── promo disclosure ──────────────────────────────────────────────
// A quiet "Have a promo code? ✦" line that expands to a parchment
// field + ghost-pill apply button. When validated, every edition
// plate re-renders to show the honoured price (or "Free") and the
// commission press skips Stripe entirely for 100%-off codes.
const PromoDisclosure = ({ open, setOpen, code, setCode, status, onApply }) => {
  const valid = status?.valid;
  const error = status?.error;
  return (
    <div style={{ maxWidth: 720, margin: '0 auto 32px', textAlign: 'center' }} data-testid="promo-disclosure">
      {!open ? (
        <button
          type="button"
          onClick={() => setOpen(true)}
          data-testid="promo-disclosure-toggle"
          style={{
            background: 'transparent', border: 'none', cursor: 'pointer',
            fontFamily: SERIF, fontStyle: 'italic',
            fontSize: 13.5, color: GOLD_MUTED,
            letterSpacing: '0.04em', padding: 6,
            textDecoration: 'underline',
            textDecorationColor: 'rgba(212,170,82,0.28)',
            textUnderlineOffset: 5,
          }}
          onMouseEnter={(e) => { e.currentTarget.style.color = GOLD; }}
          onMouseLeave={(e) => { e.currentTarget.style.color = GOLD_MUTED; }}
        >
          Have a promo code? ✦
        </button>
      ) : (
        <div style={{
          display: 'flex', flexDirection: 'column', alignItems: 'center',
          gap: 10,
        }}>
          <div style={{
            fontFamily: SERIF, fontStyle: 'italic',
            fontSize: 11, letterSpacing: '0.24em', textTransform: 'uppercase',
            color: GOLD_MUTED,
          }}>
            ✦ HONOUR A COMMISSION CODE
          </div>
          <div style={{
            display: 'flex', gap: 8, width: '100%', maxWidth: 460,
            flexWrap: 'wrap', justifyContent: 'center',
          }}>
            <input
              type="text"
              value={code}
              onChange={(e) => setCode(e.target.value.toUpperCase())}
              onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); onApply(); } }}
              placeholder="ENTER CODE"
              data-testid="promo-input"
              autoComplete="off"
              autoCapitalize="characters"
              spellCheck={false}
              style={{
                flex: 1, minWidth: 200,
                padding: '12px 16px',
                borderRadius: 4,
                background: 'rgba(20,12,6,0.78)',
                border: `1px solid ${valid ? GOLD : 'rgba(212,170,82,0.32)'}`,
                color: IVORY,
                fontFamily: SERIF, fontSize: 14,
                letterSpacing: '0.12em',
                outline: 'none',
                textAlign: 'center',
              }}
              onFocus={(e) => { if (!valid) e.currentTarget.style.borderColor = GOLD; }}
              onBlur={(e) => { if (!valid) e.currentTarget.style.borderColor = 'rgba(212,170,82,0.32)'; }}
            />
            <button
              type="button"
              onClick={onApply}
              disabled={status === 'validating' || !code.trim()}
              data-testid="promo-apply"
              style={{
                padding: '12px 22px', borderRadius: 999,
                background: 'transparent',
                border: `1px solid ${GOLD}`,
                color: IVORY,
                fontFamily: SERIF, fontStyle: 'italic',
                fontSize: 14, letterSpacing: '0.02em',
                cursor: (status === 'validating' || !code.trim()) ? 'not-allowed' : 'pointer',
                opacity: !code.trim() ? 0.5 : 1,
                transition: 'background 220ms ease',
              }}
              onMouseEnter={(e) => { if (code.trim()) e.currentTarget.style.background = 'rgba(212,170,82,0.10)'; }}
              onMouseLeave={(e) => { e.currentTarget.style.background = 'transparent'; }}
            >
              {status === 'validating' ? 'Checking…' : valid ? 'Re-apply ✦' : 'Apply ✦'}
            </button>
          </div>
          {valid && (
            <p
              data-testid="promo-applied"
              style={{
                margin: '2px 0 0', fontFamily: SERIF, fontStyle: 'italic',
                fontSize: 13.5, color: GOLD, letterSpacing: '0.02em',
              }}
            >
              ✦ {status.discount >= 100
                ? 'Honoured in full — your commission will be entrusted without payment.'
                : `Honoured — ${status.discount}% off this commission.`}
            </p>
          )}
          {error && (
            <p
              data-testid="promo-error"
              style={{
                margin: '2px 0 0', fontFamily: SERIF, fontStyle: 'italic',
                fontSize: 13, color: '#FFAA90', letterSpacing: '0.02em',
              }}
            >{error}</p>
          )}
        </div>
      )}
    </div>
  );
};

// ── edition plate (archival, NOT pricing-grid) ────────────────────
const EditionPlate = ({ edition, busy, commissionStage, promo, onChoose }) => {
  // The CTA label transitions through the commission cadence:
  //   idle      → "Commission The Room ✦"
  //   pressed   → same label, subtle inward settle, gold softens
  //   preparing → "The commission is being prepared ✦" (italic, dimmer)
  //
  // The "preparing" copy is intentionally distinct from — and
  // harmonised with — the veil's "The commission is being entrusted ✦".
  // One is internal preparation; the other is the ceremonial handoff.
  const isPreparing = commissionStage === 'preparing';
  const isPressed = commissionStage === 'pressed';
  const ctaLabel = isPreparing
    ? 'The commission is being prepared ✦'
    : `Commission ${edition.name.replace(' ✦', '')} ✦`;

  return (
  <article
    data-testid={edition.testId}
    style={{
      position: 'relative',
      background: edition.featured ? PARCHMENT_TINT : 'linear-gradient(180deg, rgba(20,12,6,0.96) 0%, rgba(12,7,4,0.96) 100%)',
      border: edition.featured
        ? '1px solid rgba(212,170,82,0.42)'
        : '1px solid rgba(212,170,82,0.18)',
      borderRadius: 4,
      padding: 'clamp(28px, 3vw, 38px) clamp(24px, 3vw, 32px) clamp(32px, 3vw, 40px)',
      display: 'flex', flexDirection: 'column',
      boxShadow: edition.featured
        ? '0 30px 70px rgba(0,0,0,0.55), 0 0 0 1px rgba(212,170,82,0.10) inset'
        : '0 20px 60px rgba(0,0,0,0.45)',
      transform: edition.featured ? 'translateY(-10px)' : 'none',
    }}
  >
    {edition.featured && (
      <div style={{
        position: 'absolute', top: 22, right: 22,
        fontFamily: SERIF, fontStyle: 'italic',
        fontSize: 10, letterSpacing: '0.24em', textTransform: 'uppercase',
        color: GOLD,
      }}>
        Most chosen ✦
      </div>
    )}
    <div style={{
      fontFamily: SERIF, fontStyle: 'italic',
      fontSize: 11, letterSpacing: '0.26em', textTransform: 'uppercase',
      color: GOLD_MUTED, marginBottom: 14,
    }}>
      {edition.eyebrow}
    </div>
    <div style={{
      fontFamily: SERIF,
      fontSize: 'clamp(24px, 2.4vw, 30px)',
      lineHeight: 1.15, fontWeight: 500,
      color: IVORY,
      letterSpacing: '-0.012em',
      marginBottom: 14,
    }}>
      {edition.name}
    </div>
    <div aria-hidden style={{
      width: 38, height: 1,
      background: 'rgba(212,170,82,0.34)',
      marginBottom: 18,
    }} />
    <p style={{
      margin: 0,
      fontFamily: SERIF, fontStyle: 'italic',
      fontSize: 15, lineHeight: 1.65,
      color: INK_DIM,
    }}>
      {edition.description}
    </p>

    {/* Explicit product list — every product named, never hidden
        behind "premium features" or "enhanced experience" vocabulary.
        Inheritance from lower editions is stated quietly above the
        new-products list so guests can see at a glance what's
        carried forward vs what's exclusive to this tier. */}
    <div style={{ marginTop: 26 }} data-testid={`${edition.testId}-products`}>
      {edition.inheritsFrom && (
        <div style={{
          fontFamily: SERIF, fontStyle: 'italic',
          fontSize: 13, color: GOLD_MUTED,
          letterSpacing: '0.01em',
          marginBottom: 12,
          lineHeight: 1.5,
        }}>
          Everything in {edition.inheritsFrom}, plus —
        </div>
      )}
      <ul style={{
        listStyle: 'none', padding: 0, margin: 0,
        display: 'flex', flexDirection: 'column', gap: 8,
      }}>
        {edition.products.map((p) => (
          <li key={p} style={{
            display: 'flex', alignItems: 'flex-start', gap: 10,
            fontFamily: SERIF,
            fontSize: 14, lineHeight: 1.55,
            color: IVORY,
          }}>
            <span aria-hidden style={{
              color: GOLD, fontSize: 12, marginTop: 4, flexShrink: 0,
            }}>✦</span>
            <span>{p}</span>
          </li>
        ))}
      </ul>
    </div>

    <div style={{ flex: 1 }} />
    <div style={{ marginTop: 30 }}>
      {promo?.valid ? (
        <div style={{ display: 'flex', alignItems: 'baseline', gap: 10, flexWrap: 'wrap' }}>
          <span style={{
            fontFamily: SERIF,
            fontSize: 'clamp(28px, 3vw, 36px)',
            color: IVORY, fontWeight: 400, letterSpacing: '-0.01em',
          }}>
            {promo.discount >= 100 ? 'Free' : `£${Math.max(0, Math.round(edition.price * (1 - promo.discount / 100)))}`}
          </span>
          <span style={{
            fontFamily: SERIF, fontStyle: 'italic',
            fontSize: 16, color: GOLD_MUTED,
            textDecoration: 'line-through',
            textDecorationColor: 'rgba(212,170,82,0.5)',
          }}>£{edition.price}</span>
        </div>
      ) : (
        <div style={{
          fontFamily: SERIF,
          fontSize: 'clamp(28px, 3vw, 36px)',
          color: IVORY, fontWeight: 400, letterSpacing: '-0.01em',
        }}>
          £{edition.price}
        </div>
      )}
      <div style={{
        fontFamily: SERIF, fontStyle: 'italic',
        fontSize: 12, color: GOLD_MUTED,
        letterSpacing: '0.16em', textTransform: 'uppercase',
        marginTop: 4,
      }}>
        {promo?.valid && promo.discount >= 100
          ? 'Honoured by your code · for the night'
          : 'Commissioned once · for the night'}
      </div>
    </div>
    <button
      type="button"
      onClick={onChoose}
      disabled={busy}
      data-testid={`${edition.testId}-cta`}
      data-commission-stage={commissionStage || 'idle'}
      style={{
        marginTop: 26,
        display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
        height: 50, padding: '0 22px', borderRadius: 999,
        background: edition.featured ? 'rgba(18,12,6,0.65)' : 'transparent',
        // Pressed state — gold border softens, reading as a tactile
        // inward acknowledgement of the tap. Preparing keeps the same
        // soft border so the chain reads continuous.
        border: isPressed || isPreparing
          ? (edition.featured
              ? '1px solid rgba(212,170,82,0.34)'
              : '1px solid rgba(212,170,82,0.20)')
          : (edition.featured
              ? '1px solid rgba(212,170,82,0.55)'
              : '1px solid rgba(212,170,82,0.30)'),
        // Preparing dims slightly so it reads as a quieter intentional
        // state, not a more-prominent loading announcement.
        color: isPreparing
          ? 'rgba(255,235,200,0.62)'
          : (edition.featured ? '#FFE7A8' : 'rgba(255,235,200,0.86)'),
        fontFamily: SERIF, fontStyle: 'italic',
        fontSize: isPreparing ? 14 : 15,
        letterSpacing: '0.01em',
        cursor: busy ? 'not-allowed' : 'pointer',
        // Subtle inward settle on press — 1px down + small opacity
        // dim, lasting ~220ms. No spinner, no loading dots.
        transform: (isPressed || isPreparing) ? 'translateY(1px)' : 'none',
        opacity: busy && !isPreparing ? 0.92 : 1,
        backdropFilter: 'blur(10px)',
        transition: 'transform 220ms ease, border-color 320ms ease, color 320ms ease, font-size 220ms ease',
      }}
      onMouseEnter={(e) => {
        if (busy) return;
        e.currentTarget.style.borderColor = 'rgba(212,170,82,0.65)';
        e.currentTarget.style.color = '#FFF1C2';
      }}
      onMouseLeave={(e) => {
        if (busy) return;
        e.currentTarget.style.borderColor = edition.featured ? 'rgba(212,170,82,0.55)' : 'rgba(212,170,82,0.30)';
        e.currentTarget.style.color = edition.featured ? '#FFE7A8' : 'rgba(255,235,200,0.86)';
      }}
    >
      {busy ? ctaLabel : `Commission ${edition.name.replace(' ✦', '')} ✦`}
    </button>
  </article>
  );
};

// ── foundational block ────────────────────────────────────────────
const FoundationBlock = () => (
  <section data-testid="pricing-foundation" style={{
    background: '#0C0704',
    padding: 'clamp(60px, 8vw, 100px) clamp(24px, 6vw, 80px)',
    borderTop: '1px solid rgba(212,170,82,0.10)',
  }}>
    <div style={{ maxWidth: 1080, margin: '0 auto' }}>
      <div style={{
        fontFamily: SERIF, fontStyle: 'italic',
        fontSize: 11, letterSpacing: '0.28em', textTransform: 'uppercase',
        color: GOLD_MUTED, marginBottom: 14, textAlign: 'center',
      }}>
        INCLUDED IN EVERY EDITION ✦
      </div>
      <h2 style={{
        margin: 0, textAlign: 'center',
        fontFamily: SERIF,
        fontSize: 'clamp(24px, 3.2vw, 36px)',
        fontWeight: 500, lineHeight: 1.18,
        letterSpacing: '-0.012em',
        color: IVORY,
        textShadow: '0 0 24px rgba(212,170,82,0.15)',
      }}>
        The foundational preservation system
      </h2>
      <p style={{
        margin: '18px auto 50px', maxWidth: 580,
        textAlign: 'center',
        fontFamily: SERIF, fontStyle: 'italic',
        fontSize: 15, lineHeight: 1.65,
        color: INK_DIM,
      }}>
        These are not features. They are how every preserved night begins.
      </p>
      <div style={{
        display: 'grid',
        gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))',
        gap: 'clamp(18px, 2vw, 28px)',
      }}>
        {FOUNDATION.map((f) => (
          <div key={f.name} style={{
            background: 'rgba(248,238,214,0.03)',
            border: '1px solid rgba(212,170,82,0.16)',
            padding: '22px 22px 24px',
            borderRadius: 4,
          }}>
            <div style={{
              fontFamily: SERIF, fontSize: 19,
              color: IVORY, marginBottom: 6,
              letterSpacing: '-0.005em',
            }}>{f.name} ✦</div>
            <div aria-hidden style={{
              width: 28, height: 1,
              background: 'rgba(212,170,82,0.30)',
              marginBottom: 12,
            }} />
            <div style={{
              fontFamily: SERIF, fontStyle: 'italic',
              fontSize: 13.5, lineHeight: 1.6,
              color: INK_DIM,
            }}>{f.desc}</div>
          </div>
        ))}
      </div>
    </div>
  </section>
);

const ClosingNote = () => (
  <section style={{
    padding: 'clamp(60px, 8vw, 100px) clamp(24px, 6vw, 80px)',
    textAlign: 'center',
  }}>
    <p style={{
      margin: '0 auto', maxWidth: 580,
      fontFamily: SERIF, fontStyle: 'italic',
      fontSize: 'clamp(15px, 1.3vw, 17px)',
      lineHeight: 1.7, color: INK_DIM,
    }}>
      Commissions are processed through Stripe \u00b7 your card details
      remain with them. EventDrop never sees them.
    </p>
  </section>
);

const Footer = () => (
  <footer style={{
    background: '#0A0503',
    padding: '52px 28px',
    borderTop: '1px solid rgba(212,170,82,0.10)',
    textAlign: 'center',
  }}>
    <div style={{
      fontFamily: SERIF, fontSize: 20, color: IVORY,
      letterSpacing: '-0.005em',
    }}>EventDrop ✦</div>
    <div style={{
      marginTop: 10,
      fontFamily: SERIF, fontStyle: 'italic',
      fontSize: 12, color: 'rgba(255,235,200,0.46)',
    }}>
      Made with care · for the nights worth keeping · © {new Date().getFullYear()}
    </div>
  </footer>
);
